import React, { useState } from "react";
import { Send, Instagram, Camera, Mail, MapPin, Phone } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Card } from "./ui/card";
import { useToast } from "../hooks/use-toast";
import { portfolioData } from "../data/mock";
import emailjs from '@emailjs/browser';

const Contact = () => {
  const { contact } = portfolioData;
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Configuration EmailJS - Web Key Studio
      const EMAILJS_CONFIG = {
        USER_ID: '0x02iuxFMGAJqBFgH', // Clé publique EmailJS
        SERVICE_ID: 'service_wmri88e', // Service Gmail configuré
        TEMPLATE_ID: 'template_kovq6yg' // Template email créé
      };

      // Paramètres du template EmailJS
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        message: formData.message,
        to_email: 'webkeystudio.contact@gmail.com',
        sent_date: new Date().toLocaleString('fr-FR')
      };

      // Vérifier si EmailJS est configuré
      if (EMAILJS_CONFIG.USER_ID === '0x02iuxFMGAJqBFgH') {
        // EmailJS est maintenant configuré !
      }

      // Envoi via EmailJS
      await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID,
        templateParams,
        EMAILJS_CONFIG.USER_ID
      );
      
      toast({
        title: "Message envoyé !",
        description: "Merci pour votre message. Je vous répondrai dans les plus brefs délais.",
      });
      
      setFormData({ name: "", email: "", message: "" });
    } catch (error) {
      console.error("Erreur EmailJS:", error);
      toast({
        title: "Erreur",
        description: "Impossible d'envoyer le message. Contactez-moi directement au 06.46.80.25.49",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-slate-800/50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-cyan-400 to-violet-500 bg-clip-text text-transparent">
              {contact.title}
            </span>
          </h2>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto mb-4">
            {contact.subtitle}
          </p>
          <p className="text-slate-500">
            {contact.description}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <Card className="bg-slate-800/50 border-slate-700 p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-slate-300 mb-2">
                    Nom complet *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="bg-slate-700/50 border-slate-600 text-white placeholder-slate-400 focus:border-cyan-500 focus:ring-cyan-500"
                    placeholder="Votre nom"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
                    Email *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="bg-slate-700/50 border-slate-600 text-white placeholder-slate-400 focus:border-cyan-500 focus:ring-cyan-500"
                    placeholder="votre@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-slate-300 mb-2">
                    Message *
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={6}
                    className="bg-slate-700/50 border-slate-600 text-white placeholder-slate-400 focus:border-cyan-500 focus:ring-cyan-500 resize-none"
                    placeholder="Décrivez votre projet..."
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-cyan-500 to-violet-500 hover:from-cyan-600 hover:to-violet-600 text-white py-3 text-lg hover:shadow-lg hover:shadow-cyan-500/25 transition-all duration-300 disabled:opacity-50"
              >
                {isSubmitting ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Envoi en cours...
                  </div>
                ) : (
                  <div className="flex items-center">
                    <Send className="mr-2" size={18} />
                    {contact.form.submitText}
                  </div>
                )}
              </Button>
            </form>
          </Card>

          {/* Contact Info */}
          <div className="space-y-8">
            {/* Social Media */}
            <Card className="bg-slate-800/50 border-slate-700 p-8">
              <h3 className="text-2xl font-bold text-white mb-6">
                Suivez-moi sur les réseaux
              </h3>
              <div className="space-y-4">
                <a
                  href={`https://instagram.com/${contact.social.instagram.replace('@', '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-4 p-4 rounded-lg bg-slate-700/50 hover:bg-slate-700 transition-colors duration-300 group"
                >
                  <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <Instagram className="text-white" size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">Instagram</h4>
                    <p className="text-slate-400">{contact.social.instagram}</p>
                  </div>
                </a>

                <a
                  href={`https://snapchat.com/add/${contact.social.snapchat.replace('@', '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-4 p-4 rounded-lg bg-slate-700/50 hover:bg-slate-700 transition-colors duration-300 group"
                >
                  <div className="w-12 h-12 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <Camera className="text-white" size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">Snapchat</h4>
                    <p className="text-slate-400">{contact.social.snapchat}</p>
                  </div>
                </a>
              </div>
            </Card>

            {/* Contact Info */}
            <Card className="bg-slate-800/50 border-slate-700 p-8">
              <h3 className="text-2xl font-bold text-white mb-6">
                Informations de contact
              </h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-4 text-slate-300">
                  <Mail className="text-cyan-400" size={20} />
                  <span>contact@webkeystudio.fr</span>
                </div>
                <div className="flex items-center space-x-4 text-slate-300">
                  <Phone className="text-violet-400" size={20} />
                  <span>{contact.phone}</span>
                </div>
                <div className="flex items-center space-x-4 text-slate-300">
                  <MapPin className="text-pink-400" size={20} />
                  <span>France - Remote</span>
                </div>
              </div>
            </Card>

            {/* Response Time */}
            <div className="bg-gradient-to-r from-cyan-500/10 to-violet-500/10 p-6 rounded-xl border border-slate-700">
              <h4 className="font-semibold text-white mb-2">Temps de réponse</h4>
              <p className="text-slate-300 text-sm">
                Je réponds généralement dans les 24h. Pour les urgences, n'hésitez pas à me contacter via les réseaux sociaux.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;